/* eslint-disable @typescript-eslint/no-non-null-assertion */
import { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router';
import Grid from '@mui/material/Grid';
import Container from '@mui/material/Container';
import { Helmet } from 'react-helmet';
import { FullScreenError, FullScreenSpinner } from 'components/FullScreen';
import FullHeight from 'components/FullHeight';
import Breadcrumbs from 'components/Breadcrumbs';
import { styled } from '@mui/material';
import { spacingSizeMap } from '@insights-ltd/design-library/themes';
import EventOverview from './EventOverview';
import EventSummary from './EventSummary';
import { useEventData } from './EventOverview/Event.helper';

type WrapperProps = {
  children: ReactElement;
};

const mobileReset = {
  height: 'auto',
  paddingBottom: 0,
  backgroundColor: 'transparent',
};

const StyledPrimaryWrapper = styled(FullHeight)(({ theme }) => ({
  paddingLeft: `calc((100vw - ${
    theme.breakpoints.values.lg + spacingSizeMap.L * 8
  }px) / 2)`,
  [theme.breakpoints.down('md')]: { ...mobileReset, paddingLeft: 0 },
}));

const StyledSecondaryWrapper = styled(FullHeight)(({ theme }) => ({
  paddingRight: `calc((100vw - ${
    theme.breakpoints.values.lg + spacingSizeMap.L * 8
  }px) / 2)`,
  [theme.breakpoints.down('md')]: { ...mobileReset, paddingRight: 0 },
}));

const Wrapper = ({ children }: WrapperProps) => (
  <StyledPrimaryWrapper backgroundColor="white">
    <StyledSecondaryWrapper>{children}</StyledSecondaryWrapper>
  </StyledPrimaryWrapper>
);

const StyledBreadcrumbWrapper = styled('div')(({ theme }) => ({
  padding: `${theme.spacing(spacingSizeMap.M)} 0`,
  backgroundColor: theme.palette.grey[100],
  borderBottom: `1px solid ${theme.palette.grey[400]}`,
}));

const Event = () => {
  const { eventId } = useParams<{ eventId: string }>();
  const { t } = useTranslation();
  const { data, status } = useEventData(eventId!);

  if (status === 'pending') {
    return <FullScreenSpinner message={t('ui.event-management.loading')} />;
  }
  if (status === 'error') {
    return (
      <FullScreenError
        message={t('ui.event-management.events.overview.error-loading-event')}
      />
    );
  }

  const [event, invitees, readExpiredIsPermitted, contributorLearners = []] =
    data;

  const eventInviteesCount = invitees?.length || 0;
  const eventBalance = event?.budget?.recoverable || 0;
  const profileCost = event?.chapters[0]?.cost || 0;

  const evaluatorNotCompletedButInvited = invitees.filter(
    ({ profile, status: inviteeStatus }) =>
      profile?.status !== 'PURCHASED' && inviteeStatus !== 'ADDED',
  ).length;

  const refundableUnits =
    eventBalance + evaluatorNotCompletedButInvited * profileCost;

  return (
    <>
      <Helmet>
        <title>
          {t('ui.event-management.title.event', { eventName: event!.name })}
        </title>
      </Helmet>
      <StyledBreadcrumbWrapper>
        <Container maxWidth="lg" sx={{ overflowWrap: 'anywhere' }}>
          <Breadcrumbs
            crumbs={{
              '/': t('ui.event-management.events.nav.home'),
              '/experiences': t('ui.event-management.events.title'),
            }}
            activeText={event!.name}
          />
        </Container>
      </StyledBreadcrumbWrapper>
      <Wrapper>
        <Grid
          container
          sx={(theme) => ({
            '> :first-of-type': {
              backgroundColor: theme.palette.grey[100],
              borderRight: `1px solid ${theme.palette.grey[400]}`,
            },
            '> *': {
              padding: theme.spacing(spacingSizeMap.S),
            },
            [theme.breakpoints.up(theme.breakpoints.values.md)]: {
              height: '100%',
              '> *': {
                padding: `${theme.spacing(spacingSizeMap.L)} !important`,
              },
            },
          })}
        >
          <Grid size={{ xs: 12, md: 4.6 }}>
            <EventSummary
              event={event!}
              eventInviteesCount={eventInviteesCount}
              eventRefundableUnits={refundableUnits}
            />
          </Grid>
          <Grid size={{ xs: 12, md: 7.4 }}>
            <EventOverview
              invitees={invitees!}
              contributorLearners={contributorLearners}
              event={event!}
              readExpiredIsPermitted={readExpiredIsPermitted}
            />
          </Grid>
        </Grid>
      </Wrapper>
    </>
  );
};

export default Event;
/* eslint-enable @typescript-eslint/no-non-null-assertion */
